<template>
  <div class="c-media-masthead">
    <div class="c-media-masthead__inner">
      <sc-text tag="h1" v-if="fields && fields.heading" :field="fields.heading" />
      <sc-text tag="h2" v-if="fields && fields.subheading" :field="fields.subheading" />
      <div class="articles">
        <div
          v-for="item in items"
          :key="item.id"
          class="featured"
          :class="{ noSubFeatured: !hasSubFeaturedContent }"
        >
          <item-image
            :image="item.item.featuredImage.jss.value"
            :alt="item.item.resourceTitle.value"
            class="featured__image"
            :class="{ padding: fields?.featuredpadding?.value === true }"
          />

          <div class="featured__content">
            <sc-text
              class="label"
              v-if="fields && fields.featuredlabel"
              :field="fields.featuredlabel"
            />
            <h3>{{ item.item.resourceTitle.value }}</h3>
            <p>
              <item-excerpt :item="item.item" :maxCharacters="145" />
            </p>

            <a
              v-if="item.item.url && fields && fields.featuredctatext"
              :href="item.item.url"
              class="c-cta--primary"
            >
              <sc-text v-if="fields && fields.featuredctatext" :field="fields.featuredctatext" />
            </a>
          </div>
        </div>

        <div class="subfeatured" v-if="items && hasSubFeaturedContent">
          <sc-text
            class="label"
            v-if="fields && fields.subfeaturedlabel"
            :field="fields.subfeaturedlabel"
          />
          <sc-text
            tag="h3"
            v-if="fields && fields.subfeaturedheading"
            :field="fields.subfeaturedheading"
          />
          <sc-text
            tag="p"
            v-if="fields && fields.subfeatureddescription"
            :field="fields.subfeatureddescription"
          />
          <sc-link
            v-if="fields && fields.subfeaturedcta"
            :field="fields.subfeaturedcta"
            class="c-cta--secondary"
            :class="fields.subfeaturedcta.value.class"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Text, Link, Image } from '@sitecore-jss/sitecore-jss-vue';
import { getCurrentInstance, reactive } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { InsightsQuery } from './Resources/CIS-Resource-PagedListing.graphql';
import ItemImage from './Resources/CIS-Resource-PagedListing-Image.vue';
import ItemExcerpt from './Resources/CIS-Resource-PagedListing-Excerpt.vue';

export default {
  name: 'CIS-Media-Masthead',
  components: {
    ScText: Text,
    ScLink: Link,
    ItemImage,
    ItemExcerpt
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    items() {
      return this.result?.search?.results?.items;
    },
    hasSubFeaturedContent() {
      return (
        this.fields?.subfeaturedlabel?.value?.length > 0 ||
        this.fields?.subfeaturedheading?.value?.length > 0 ||
        this.fields?.subfeatureddescription?.value?.length > 0 ||
        this.fields?.subfeaturedcta?.value?.text?.length > 0
      );
    }
  },
  setup(props) {
    const instance = getCurrentInstance();
    const properties = instance.appContext.config.globalProperties.$jss;

    // Use the source path if available, otherwise use the page's path.
    const rootItem = `/sitecore/Content/cisecurity/ngen/home${
      props?.fields?.source?.url || properties.sitecoreContext()?.itemPath
    }`;

    const sortBy = props?.fields?.sortByName?.value
      ? '_name'
      : !props?.fields?.useDateIssued?.value
      ? 'publishedAt'
      : 'dateIssued';
    // If sorting by name then sort acending, otherwise sort decending
    const sortDesc = !props?.fields?.sortByName?.value;

    const data = reactive({
      rootItem: rootItem,
      page: 1,
      pageSize: 1,
      sortBy: sortBy,
      sortDesc: sortDesc,
      selectedTopicIds: []
    });

    const variables = () => {
      const ownerIds =
        data.selectedTopicIds?.length > 0 ? data.selectedTopicIds.join(',') : undefined;
      const fieldsEqual = ownerIds ? [{ name: 'owners', value: ownerIds }] : null;

      const output = {
        ...data,
        after: ((data.page - 1) * data.pageSize).toString(),
        fieldsEqual: fieldsEqual
      };

      return output;
    };

    const queryOptions = () => {
      return {
        prefetch: true
      };
    };

    const { result, loading, error, onResult } = useQuery(InsightsQuery, variables, queryOptions);

    onResult((result) => {
      // If the set 'page' is out of bounds then reset page to 1
      if (
        !result.loading &&
        data.page > 1 &&
        result?.data?.search?.results?.pageInfo?.endCursor === '0' &&
        result?.data?.search?.results?.pageInfo?.startCursor === '0'
      ) {
        data.page = 1;
      }
    });

    return {
      data,
      result,
      loading,
      error
    };
  }
};
</script>
<style lang="scss" scoped>
.c-media-masthead {
  @include padding-y(3rem, 0);
  color: $white;
  background: linear-gradient(
    169deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 59, 92, 1) 20%,
    rgba(0, 134, 191, 1) 100%
  );
  margin-bottom: 5rem;

  @include tablet-up {
    @include padding-y(5rem, 0);
    margin-bottom: 8rem;
  }

  &__inner {
    @include padding-x-thin;
  }

  &__inner {
    h1,
    h2 {
      color: $white;
      -webkit-text-fill-color: $white;
      font-weight: 200;
    }

    h1 {
      margin-top: 0;

      @include phone-only {
        font-size: 3.6rem;
        line-height: 4.2rem;
      }
    }

    h2 {
      @include phone-only {
        font-size: 2.4rem;
        line-height: 3rem;
      }
    }

    h3 {
      color: $dark-blue;
      @include phone-only {
        font-size: 2rem;
        line-height: 2.6rem;
      }
    }

    p {
      color: $dark-blue;
      line-height: 2rem;
      margin-bottom: 2rem;

      @include desktop-up {
        line-height: 2.6rem;
        margin-bottom: 2.4rem;
      }
    }
  }

  .articles {
    margin-bottom: 2rem;

    @include desktop-up {
      display: flex;
      margin-top: 4rem;
    }

    .label {
      position: absolute;
      top: 0;
      left: 0;
      display: inline-block;
      background-color: $dark-blue;
      border-radius: 0;
      font-weight: 500;
      font-size: 1.4rem;
      line-height: 1.6rem;
      letter-spacing: 0.1rem;
      text-transform: uppercase;
      padding: 0.3rem 0.8rem 0.6rem 1rem;
    }

    .featured {
      border-top: solid 0.5rem $orange;
      background-color: $white;
      box-shadow: 0 17px 18px 0 rgba(0, 0, 0, 0.08), 0 3px 8px 0 rgba(0, 0, 0, 0.08);
      margin-bottom: 0.8rem;

      @include tablet-up {
        display: flex;
      }

      @include desktop-up {
        width: 75%;
        margin-right: 1.2rem;
        margin-bottom: 0;
      }

      &.noSubFeatured {
        @include desktop-up {
          width: 100%;
          margin-right: 0;
        }
      }

      &__image {
        width: 100%;
        margin-right: 0;
        flex-shrink: initial;

        @include desktop-up {
          max-width: 47rem;
        }

        &.padding {
          padding-left: 2rem;
          padding-right: 2rem;
        }

        &:deep() {
          a {
            width: 100%;
            height: 100%;
            max-height: 16rem;
            pointer-events: none;
          }

          img {
            width: 100%;
            height: 100%;
            max-height: 16rem;
            object-fit: cover;

            @include tablet-up {
              max-height: none;
              object-fit: contain;
            }
          }
        }
      }

      &__content {
        position: relative;
        padding: 4.5rem 2.1rem 2.1rem 2.2rem;

        @include desktop-up {
          width: 100%;
          padding: 5.4rem 2.4rem 4rem 2.4rem;
        }
      }

      .c-list-excerpt {
        display: block;
        font-weight: 200;
      }

      &:deep() {
        .c-list-excerpt {
          display: block;
          font-weight: 200;
        }
      }
    }

    .subfeatured {
      position: relative;
      border-top: solid 0.5rem $orange;
      background-color: $white;
      box-shadow: 0 17px 18px 0 rgba(0, 0, 0, 0.08), 0 3px 8px 0 rgba(0, 0, 0, 0.08);

      padding: 4.5rem 2.1rem 2.1rem 2.2rem;

      @include desktop-up {
        width: 25%;
        padding: 5.4rem 2.4rem 3rem 2.4rem;
      }
    }

    .c-cta--primary,
    .c-cta--secondary {
      margin-bottom: 0;
    }
  }
}
</style>
